import React, { useState, useEffect, useCallback, useRef } from 'react';
import { GoogleMap, LoadScript, Polyline, Marker } from '@react-google-maps/api';
import { 
  Play, 
  Pause, 
  Square, 
  MapPin, 
  Clock, 
  Gauge, 
  Mountain, 
  Footprints,
  Battery,
  Wifi,
  WifiOff,
  Camera,
  Save,
  Settings,
  Plus,
  X,
  Upload,
  Crown,
  ArrowRight,
  Shield,
  Zap,
  Navigation
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import HighlightsManager from '../components/HighlightsManager';
import { useAuth } from '../contexts/AuthContext';
import { categories, difficulties } from '../data/mock';
import axios from 'axios';

const mapContainerStyle = {
  width: '100%',
  height: '70vh'
};

const defaultCenter = {
  lat: 34.0522,
  lng: -118.2437
};

// Demo GPS coordinates for a realistic walk in Los Angeles (Silver Lake area)
const demoGPSCoordinates = [
  { lat: 34.0928, lng: -118.2747, altitude: 150, accuracy: 3, speed: 0, heading: 0 },
  { lat: 34.0930, lng: -118.2745, altitude: 152, accuracy: 4, speed: 1.2, heading: 45 },
  { lat: 34.0932, lng: -118.2743, altitude: 154, accuracy: 3, speed: 1.4, heading: 50 },
  { lat: 34.0935, lng: -118.2740, altitude: 156, accuracy: 5, speed: 1.1, heading: 60 },
  { lat: 34.0938, lng: -118.2738, altitude: 158, accuracy: 4, speed: 1.3, heading: 45 },
  { lat: 34.0940, lng: -118.2735, altitude: 160, accuracy: 3, speed: 1.5, heading: 40 },
  { lat: 34.0943, lng: -118.2732, altitude: 162, accuracy: 4, speed: 1.2, heading: 35 },
  { lat: 34.0945, lng: -118.2729, altitude: 164, accuracy: 5, speed: 1.4, heading: 30 },
  { lat: 34.0948, lng: -118.2726, altitude: 166, accuracy: 3, speed: 1.6, heading: 25 },
  { lat: 34.0950, lng: -118.2723, altitude: 168, accuracy: 4, speed: 1.3, heading: 20 },
  { lat: 34.0952, lng: -118.2720, altitude: 170, accuracy: 5, speed: 1.1, heading: 15 },
  { lat: 34.0954, lng: -118.2717, altitude: 172, accuracy: 3, speed: 1.5, heading: 10 },
  { lat: 34.0956, lng: -118.2714, altitude: 174, accuracy: 4, speed: 1.4, heading: 5 },
  { lat: 34.0958, lng: -118.2711, altitude: 176, accuracy: 5, speed: 1.2, heading: 0 },
  { lat: 34.0960, lng: -118.2708, altitude: 178, accuracy: 3, speed: 1.3, heading: 355 },
  { lat: 34.0962, lng: -118.2705, altitude: 180, accuracy: 4, speed: 1.6, heading: 350 },
  { lat: 34.0964, lng: -118.2702, altitude: 182, accuracy: 5, speed: 1.1, heading: 345 },
  { lat: 34.0966, lng: -118.2699, altitude: 184, accuracy: 3, speed: 1.4, heading: 340 },
  { lat: 34.0968, lng: -118.2696, altitude: 186, accuracy: 4, speed: 1.5, heading: 335 },
  { lat: 34.0970, lng: -118.2693, altitude: 188, accuracy: 5, speed: 1.2, heading: 330 },
  { lat: 34.0972, lng: -118.2690, altitude: 190, accuracy: 3, speed: 1.3, heading: 325 },
  { lat: 34.0974, lng: -118.2687, altitude: 192, accuracy: 4, speed: 1.6, heading: 320 },
  { lat: 34.0976, lng: -118.2684, altitude: 194, accuracy: 5, speed: 1.1, heading: 315 },
  { lat: 34.0978, lng: -118.2681, altitude: 196, accuracy: 3, speed: 1.4, heading: 310 },
  { lat: 34.0980, lng: -118.2678, altitude: 198, accuracy: 4, speed: 1.5, heading: 305 }
];

const LiveTrackingPage = () => {
  const { user } = useAuth();
  const [trackingSession, setTrackingSession] = useState(null);
  const [isTracking, setIsTracking] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [coordinates, setCoordinates] = useState([]);
  const [currentPosition, setCurrentPosition] = useState(null);
  const [stats, setStats] = useState({
    distance_meters: 0,
    duration_seconds: 0,
    average_speed_ms: 0,
    max_speed_ms: 0,
    elevation_gain_meters: 0,
    calories_burned: 0
  });
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [batteryLevel, setBatteryLevel] = useState(null);
  const [trackingSettings, setTrackingSettings] = useState({
    gps_interval: 5000, // 5 seconds
    high_accuracy: true,
    battery_saving: false
  });
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [isDemoMode, setIsDemoMode] = useState(false);
  const [demoIndex, setDemoIndex] = useState(0);
  const [nearbyPOIs, setNearbyPOIs] = useState([]);
  const [poiNotifications, setPOINotifications] = useState([]);
  const [discoveredPOIs, setDiscoveredPOIs] = useState([]);
  const [walkDetails, setWalkDetails] = useState({
    title: '',
    description: '',
    practicalSummary: '',
    startingPoint: '',
    endingPoint: '',
    category: '',
    difficulty: '',
    transitAccess: '',
    bestTimes: '',
    accessibility: {
      strollerFriendly: false,
      wheelchairAccessible: false,
      parkingAvailable: false,
      restrooms: false,
      shadeCoverage: ''
    },
    tags: [],
    privacy: 'private',
    make_public: false
  });
  const [photos, setPhotos] = useState([]);
  const [selectedHighlights, setSelectedHighlights] = useState([]);

  // Helper functions for modal
  const handleWalkDetailChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setWalkDetails(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setWalkDetails(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const handlePhotoUpload = (e) => {
    const files = Array.from(e.target.files);
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = (event) => {
        setPhotos(prev => [...prev, {
          id: Date.now() + Math.random(),
          url: event.target.result,
          file: file,
          data: event.target.result // For backend compatibility
        }]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removePhoto = (photoId) => {
    setPhotos(prev => prev.filter(photo => photo.id !== photoId));
  };

  const watchIdRef = useRef(null);
  const startTimeRef = useRef(null);
  const lastPositionRef = useRef(null);
  const offlineDataRef = useRef([]);
  const intervalRef = useRef(null);

  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    // Simulate script loading
    setIsLoaded(true);
  }, []);

  // Check for existing active session on mount
  useEffect(() => {
    checkActiveSession();
    setupBatteryMonitoring();
    
    // Online/offline event listeners
    const handleOnline = () => {
      setIsOnline(true);
      syncOfflineData();
    };
    const handleOffline = () => setIsOnline(false);
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      if (watchIdRef.current) {
        navigator.geolocation.clearWatch(watchIdRef.current);
      }
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  const checkActiveSession = async () => {
    if (!user) return;
    
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/tracking/active`,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` }
        }
      );
      
      if (response.data) {
        setTrackingSession(response.data);
        setIsTracking(response.data.status === 'active');
        setIsPaused(response.data.status === 'paused');
        setCoordinates(response.data.coordinates || []);
        setStats(response.data.stats || stats);
        
        if (response.data.status === 'active') {
          resumeTracking();
        }
      }
    } catch (error) {
      console.error('Error checking active session:', error);
    }
  };

  const setupBatteryMonitoring = async () => {
    if ('getBattery' in navigator) {
      try {
        const battery = await navigator.getBattery();
        setBatteryLevel(Math.round(battery.level * 100));
        
        battery.addEventListener('levelchange', () => {
          setBatteryLevel(Math.round(battery.level * 100));
        });
      } catch (error) {
        console.log('Battery API not available');
      }
    }
  };

  const startTracking = async () => {
    if (!user) {
      alert('Please log in to start tracking');
      return;
    }

    try {
      // Start tracking session on backend
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/tracking/start`,
        {
          session_name: `Walk ${new Date().toLocaleString()}`,
          settings: trackingSettings
        },
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` }
        }
      );

      setTrackingSession(response.data);
      setIsTracking(true);
      setIsPaused(false);
      startTimeRef.current = new Date();
      
      // Start GPS tracking (real or demo)
      if (isDemoMode) {
        startDemoTracking();
      } else {
        startGPSTracking();
      }
      
      // Start timer for duration updates
      intervalRef.current = setInterval(updateDuration, 1000);
      
    } catch (error) {
      console.error('Error starting tracking:', error);
      alert('Failed to start tracking session');
    }
  };

  const startDemoMode = async () => {
    setIsDemoMode(true);
    await startTracking();
  };

  const startDemoTracking = () => {
    setDemoIndex(0);
    
    // Simulate GPS updates every 3 seconds (faster for demo)
    const demoInterval = setInterval(() => {
      if (demoIndex < demoGPSCoordinates.length - 1) {
        const currentCoord = demoGPSCoordinates[demoIndex];
        const newCoord = {
          ...currentCoord,
          timestamp: new Date().toISOString()
        };
        
        handlePositionUpdate({
          coords: {
            latitude: newCoord.lat,
            longitude: newCoord.lng,
            altitude: newCoord.altitude,
            accuracy: newCoord.accuracy,
            speed: newCoord.speed,
            heading: newCoord.heading
          }
        }, true); // Pass true to indicate this is demo data
        
        setDemoIndex(prev => prev + 1);
      } else {
        // Demo completed, can loop or stop
        clearInterval(demoInterval);
        console.log('Demo GPS tracking completed');
      }
    }, 3000); // Update every 3 seconds for demo
    
    // Store interval reference for cleanup
    watchIdRef.current = demoInterval;
  };

  const startGPSTracking = () => {
    if (!navigator.geolocation) {
      alert('Geolocation is not supported by this browser');
      return;
    }

    const options = {
      enableHighAccuracy: trackingSettings.high_accuracy,
      timeout: 10000,
      maximumAge: trackingSettings.battery_saving ? 10000 : 1000
    };

    watchIdRef.current = navigator.geolocation.watchPosition(
      handlePositionUpdate,
      handlePositionError,
      options
    );
  };

  const handlePositionUpdate = (position, isDemo = false) => {
    const newCoord = {
      lat: position.coords.latitude,
      lng: position.coords.longitude,
      altitude: position.coords.altitude,
      accuracy: position.coords.accuracy,
      timestamp: new Date().toISOString(),
      speed: position.coords.speed || 0,
      heading: position.coords.heading
    };

    setCurrentPosition(newCoord);
    
    // Add to coordinates array
    const newCoordinates = [...coordinates, newCoord];
    setCoordinates(newCoordinates);
    
    // Update stats
    updateStats(newCoordinates);
    
    // Discover nearby POIs every few coordinates to avoid too many API calls
    if (newCoordinates.length % 5 === 0) {
      discoverNearbyPOIs(newCoord);
    }
    
    // Store offline if needed (don't send demo data to backend immediately)
    if (!isDemo && !isOnline) {
      offlineDataRef.current.push(newCoord);
    } else if (!isDemo) {
      // Send to backend immediately if online and real data
      sendCoordinatesUpdate([newCoord]);
    }
    
    // For demo mode, batch send coordinates every 5 updates
    if (isDemo && coordinates.length % 5 === 0) {
      sendCoordinatesUpdate(newCoordinates.slice(-5));
    }
  };

  const discoverNearbyPOIs = async (currentCoord) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/cultural-pois/search`,
        {
          params: {
            lat: currentCoord.lat,
            lng: currentCoord.lng,
            radius: 50, // 50m radius for notifications
            featured_only: false,
            limit: 10
          }
        }
      );

      const pois = response.data;
      
      // Check for new POIs we haven't discovered yet
      const newPOIs = pois.filter(poi => 
        !discoveredPOIs.some(discovered => discovered.id === poi.id)
      );

      if (newPOIs.length > 0) {
        setDiscoveredPOIs(prev => [...prev, ...newPOIs]);
        
        // Show notifications for interesting POIs
        newPOIs.forEach(poi => {
          if (poi.featured || poi.user_ratings >= 4.0) {
            showPOINotification(poi);
          }
        });
      }

      setNearbyPOIs(pois);
    } catch (error) {
      console.error('Error discovering POIs:', error);
    }
  };

  const showPOINotification = (poi) => {
    const notification = {
      id: Date.now() + Math.random(),
      poi,
      timestamp: new Date()
    };

    setPOINotifications(prev => [...prev, notification]);

    // Auto-remove notification after 10 seconds
    setTimeout(() => {
      setPOINotifications(prev => prev.filter(n => n.id !== notification.id));
    }, 10000);
  };

  const dismissPOINotification = (notificationId) => {
    setPOINotifications(prev => prev.filter(n => n.id !== notificationId));
  };

  const handlePositionError = (error) => {
    console.error('GPS Error:', error.message);
    
    switch (error.code) {
      case error.PERMISSION_DENIED:
        alert('GPS access denied. Please enable location services.');
        break;
      case error.POSITION_UNAVAILABLE:
        alert('GPS position unavailable. Please check your device settings.');
        break;
      case error.TIMEOUT:
        console.log('GPS timeout - will retry');
        break;
      default:
        console.log('Unknown GPS error');
    }
  };

  const sendCoordinatesUpdate = async (coords) => {
    if (!trackingSession || !isOnline) return;

    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/tracking/${trackingSession.id}/update`,
        {
          coordinates: coords,
          current_stats: stats
        },
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` }
        }
      );
    } catch (error) {
      console.error('Error updating tracking session:', error);
      // Store in offline queue for later sync
      offlineDataRef.current.push(...coords);
    }
  };

  const syncOfflineData = async () => {
    if (offlineDataRef.current.length > 0 && trackingSession) {
      try {
        await sendCoordinatesUpdate(offlineDataRef.current);
        offlineDataRef.current = [];
      } catch (error) {
        console.error('Error syncing offline data:', error);
      }
    }
  };

  const updateStats = (coords) => {
    if (coords.length < 2) return;

    let totalDistance = 0;
    let elevationGain = 0;
    let maxSpeed = 0;

    for (let i = 1; i < coords.length; i++) {
      const prev = coords[i - 1];
      const curr = coords[i];
      
      // Calculate distance using Haversine formula
      const distance = calculateDistance(prev.lat, prev.lng, curr.lat, curr.lng);
      totalDistance += distance;
      
      // Track elevation gain
      if (prev.altitude && curr.altitude && curr.altitude > prev.altitude) {
        elevationGain += curr.altitude - prev.altitude;
      }
      
      // Track max speed
      if (curr.speed > maxSpeed) {
        maxSpeed = curr.speed;
      }
    }

    const duration = startTimeRef.current ? 
      Math.floor((new Date() - startTimeRef.current) / 1000) : 0;
    
    const averageSpeed = duration > 0 ? totalDistance / duration : 0;
    const caloriesBurned = Math.floor(totalDistance * 0.04); // Rough estimate

    setStats({
      distance_meters: totalDistance,
      duration_seconds: duration,
      average_speed_ms: averageSpeed,
      max_speed_ms: maxSpeed,
      elevation_gain_meters: elevationGain,
      calories_burned: caloriesBurned
    });
  };

  const calculateDistance = (lat1, lng1, lat2, lng2) => {
    const R = 6371000; // Earth's radius in meters
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLng = (lng2 - lng1) * Math.PI / 180;
    const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
              Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
              Math.sin(dLng/2) * Math.sin(dLng/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  };

  const updateDuration = () => {
    if (startTimeRef.current && isTracking && !isPaused) {
      const duration = Math.floor((new Date() - startTimeRef.current) / 1000);
      setStats(prev => ({ ...prev, duration_seconds: duration }));
    }
  };

  const pauseTracking = async () => {
    if (!trackingSession) return;

    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/tracking/${trackingSession.id}/pause`,
        {},
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` }
        }
      );

      setIsPaused(true);
      setIsTracking(false);
      
      if (watchIdRef.current) {
        navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
      }
      
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    } catch (error) {
      console.error('Error pausing tracking:', error);
    }
  };

  const resumeTracking = async () => {
    if (!trackingSession) return;

    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/tracking/${trackingSession.id}/resume`,
        {},
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` }
        }
      );

      setIsPaused(false);
      setIsTracking(true);
      
      // Resume GPS tracking (real or demo)
      if (isDemoMode) {
        startDemoTracking();
      } else {
        startGPSTracking();
      }
      
      intervalRef.current = setInterval(updateDuration, 1000);
    } catch (error) {
      console.error('Error resuming tracking:', error);
    }
  };

  const stopTracking = () => {
    setShowCompleteModal(true);
  };

  const completeWalk = async () => {
    if (!trackingSession) return;

    try {
      // Sync any remaining offline data
      await syncOfflineData();
      
      // Prepare enhanced walk data
      const enhancedWalkData = {
        ...walkDetails,
        photos: photos.map(photo => ({
          id: photo.id,
          type: 'photo',
          data: photo.data,
          filename: photo.file?.name || 'photo.jpg'
        })),
        poi_discoveries: discoveredPOIs.map(poi => poi.id), // Add discovered POI IDs
        walk_highlights: selectedHighlights // Add selected walk highlights (both regular and cultural)
      };
      
      // Complete the tracking session
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/tracking/${trackingSession.id}/complete`,
        enhancedWalkData,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` }
        }
      );

      // Clean up
      if (watchIdRef.current) {
        navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
      }
      
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }

      // Reset state
      setTrackingSession(null);
      setIsTracking(false);
      setIsPaused(false);
      setCoordinates([]);
      setCurrentPosition(null);
      setStats({
        distance_meters: 0,
        duration_seconds: 0,
        average_speed_ms: 0,
        max_speed_ms: 0,
        elevation_gain_meters: 0,
        calories_burned: 0
      });
      setPhotos([]);
      setSelectedHighlights([]);
      setDiscoveredPOIs([]);
      setShowCompleteModal(false);
      
      alert(`Walk "${walkDetails.title}" saved successfully with all details!`);
      
    } catch (error) {
      console.error('Error completing walk:', error);
      alert('Failed to save walk. Please try again.');
    }
  };

  const formatDuration = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  const formatDistance = (meters) => {
    if (meters >= 1000) {
      return `${(meters / 1000).toFixed(2)} km`;
    }
    return `${Math.round(meters)} m`;
  };

  const formatSpeed = (ms) => {
    const kmh = ms * 3.6;
    return `${kmh.toFixed(1)} km/h`;
  };

  const pathCoordinates = coordinates.map(coord => ({
    lat: coord.lat,
    lng: coord.lng
  }));

  if (!isLoaded) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading GPS tracking...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      {/* Coming Soon Premium Gate */}
      <section className="relative bg-gradient-to-br from-slate-900 via-blue-900 to-purple-900 text-white py-20 overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmZmZmYiIGZpbGwtb3BhY2l0eT0iMC4xIj48Y2lyY2xlIGN4PSIyMCIgY3k9IjIwIiByPSIzIi8+PC9nPjwvZz48L3N2Zz4=')]"></div>
        <div className="relative max-w-4xl mx-auto px-4 text-center">
          <div className="w-20 h-20 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full flex items-center justify-center mx-auto mb-8">
            <Navigation className="h-10 w-10 text-black" />
          </div>
          <h1 className="text-4xl md:text-5xl font-black mb-6">
            Live GPS Tracking
            <span className="block pb-2 text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-orange-500 mt-2">
              Coming Soon
            </span>
          </h1>
          <p className="text-xl text-blue-100 mb-10 max-w-2xl mx-auto leading-relaxed">
            Record your walks in real-time with GPS tracking, live stats, and automatic route mapping.
            Coming to the Wanderloop app — founding members get it first.
          </p>
          <div className="flex flex-col sm:flex-row gap-6 justify-center items-center mb-8">
            <Link to="/membership">
              <Button
                size="lg"
                className="bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 text-black font-black px-12 py-4 text-xl shadow-2xl transform hover:scale-105 transition-all duration-300"
              >
                <Crown className="mr-3 h-6 w-6" />
                Become a Founding Member
                <ArrowRight className="ml-3 h-6 w-6" />
              </Button>
            </Link>
          </div>
          <div className="flex items-center justify-center gap-8 text-sm text-blue-100">
            <div className="flex items-center gap-2">
              <Shield className="w-4 h-4" />
              <span>Cancel anytime</span>
            </div>
            <div className="flex items-center gap-2">
              <Zap className="w-4 h-4" />
              <span>Founding rate locked in</span>
            </div>
          </div>
        </div>
      </section>

      {/* Preview of GPS Tracking UI */}
      <div className="relative">
        {/* Locked overlay */}
        <div className="absolute inset-0 bg-white/40 backdrop-blur-[2px] z-10 pointer-events-none flex items-center justify-center">
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl px-8 py-6 shadow-lg flex flex-col items-center">
            <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center mb-3">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                <path d="M7 11V7a5 5 0 0110 0v4"/>
              </svg>
            </div>
            <p className="text-sm font-semibold text-gray-700">Premium Feature Preview</p>
            <p className="text-xs text-gray-500 mt-1">Available for Wanderloop+ members</p>
          </div>
        </div>
        <div className="opacity-60 pointer-events-none select-none">

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header Section */}
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Live GPS Tracking</h1>
            <p className="text-gray-600">Track your walk in real-time and save it to your collection</p>
          </div>
          
          {/* Status Indicators */}
          <div className="flex items-center space-x-4 mt-4 lg:mt-0">
            <div className="flex items-center space-x-2">
              {isOnline ? (
                <Wifi className="h-5 w-5 text-green-500" />
              ) : (
                <WifiOff className="h-5 w-5 text-red-500" />
              )}
              <span className="text-sm text-gray-600">
                {isOnline ? 'Online' : 'Offline'}
              </span>
            </div>
            
            {batteryLevel !== null && (
              <div className="flex items-center space-x-2">
                <Battery className="h-5 w-5 text-gray-500" />
                <span className="text-sm text-gray-600">{batteryLevel}%</span>
              </div>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Map Section */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader className="pb-4">
                <div className="flex justify-between items-center">
                  <CardTitle className="text-lg">Live Route Map</CardTitle>
                  <div className="flex gap-2">
                    {trackingSession && (
                      <Badge variant={isTracking ? "default" : isPaused ? "secondary" : "outline"}>
                        {isTracking ? "Recording" : isPaused ? "Paused" : "Stopped"}
                      </Badge>
                    )}
                    {isDemoMode && (
                      <Badge variant="outline" className="bg-blue-50 text-blue-700">
                        Demo Mode
                      </Badge>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <LoadScript
                  googleMapsApiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}
                  libraries={['geometry']}
                >
                  <GoogleMap
                    mapContainerStyle={mapContainerStyle}
                    center={currentPosition || defaultCenter}
                    zoom={16}
                    options={{
                      zoomControl: true,
                      streetViewControl: false,
                      mapTypeControl: true,
                      fullscreenControl: false
                    }}
                  >
                    {/* Route Polyline */}
                    {pathCoordinates.length > 1 && (
                      <Polyline
                        path={pathCoordinates}
                        options={{
                          strokeColor: "#3B82F6",
                          strokeOpacity: 0.8,
                          strokeWeight: 4
                        }}
                      />
                    )}
                    
                    {/* Current Position Marker */}
                    {currentPosition && (
                      <Marker
                        position={currentPosition}
                        icon={{
                          url: "data:image/svg+xml;charset=UTF-8," + encodeURIComponent(`
                            <svg width="20" height="20" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                              <circle cx="10" cy="10" r="8" fill="#3B82F6" stroke="white" stroke-width="2"/>
                              <circle cx="10" cy="10" r="3" fill="white"/>
                            </svg>
                          `),
                          scaledSize: { width: 20, height: 20 }
                        }}
                      />
                    )}
                    
                    {/* Start Position Marker */}
                    {pathCoordinates.length > 0 && (
                      <Marker
                        position={pathCoordinates[0]}
                        icon={{
                          url: "data:image/svg+xml;charset=UTF-8," + encodeURIComponent(`
                            <svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                              <circle cx="12" cy="12" r="10" fill="#10B981" stroke="white" stroke-width="2"/>
                              <text x="12" y="16" text-anchor="middle" fill="white" font-size="12" font-weight="bold">S</text>
                            </svg>
                          `)
                        }}
                      />
                    )}
                  </GoogleMap>
                </LoadScript>
              </CardContent>
            </Card>
          </div>

          {/* Controls and Stats Section */}
          <div className="space-y-6">
            {/* Control Buttons */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Controls</CardTitle>
                {isDemoMode && (
                  <Badge variant="secondary" className="w-fit">
                    Demo Mode Active
                  </Badge>
                )}
              </CardHeader>
              <CardContent className="space-y-4">
                {!trackingSession ? (
                  <div className="space-y-3">
                    <Button 
                      onClick={startTracking} 
                      className="w-full bg-green-600 hover:bg-green-700"
                      disabled={!user}
                    >
                      <Play className="h-5 w-5 mr-2" />
                      Start GPS Tracking
                    </Button>
                    
                    <div className="relative">
                      <div className="absolute inset-0 flex items-center">
                        <span className="w-full border-t" />
                      </div>
                      <div className="relative flex justify-center text-xs uppercase">
                        <span className="bg-white px-2 text-gray-500">OR</span>
                      </div>
                    </div>
                    
                    <Button 
                      onClick={startDemoMode} 
                      variant="outline"
                      className="w-full border-blue-200 text-blue-600 hover:bg-blue-50"
                      disabled={!user}
                    >
                      <Play className="h-5 w-5 mr-2" />
                      Try Demo Mode
                    </Button>
                    
                    <div className="bg-blue-50 p-3 rounded-lg">
                      <p className="text-xs text-blue-800">
                        <strong>Demo Mode:</strong> Simulates GPS tracking with realistic LA coordinates. 
                        Perfect for testing in preview environments.
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {isTracking ? (
                      <Button 
                        onClick={pauseTracking} 
                        className="w-full bg-yellow-600 hover:bg-yellow-700"
                      >
                        <Pause className="h-5 w-5 mr-2" />
                        Pause
                      </Button>
                    ) : isPaused ? (
                      <Button 
                        onClick={resumeTracking} 
                        className="w-full bg-green-600 hover:bg-green-700"
                      >
                        <Play className="h-5 w-5 mr-2" />
                        Resume
                      </Button>
                    ) : null}
                    
                    <Button 
                      onClick={stopTracking} 
                      className="w-full bg-red-600 hover:bg-red-700"
                      disabled={coordinates.length === 0}
                    >
                      <Square className="h-5 w-5 mr-2" />
                      Finish & Save
                    </Button>
                  </div>
                )}
                
                {!user && (
                  <p className="text-sm text-gray-500 text-center">
                    Please log in to start tracking walks
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Live Stats */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Live Stats</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center">
                    <div className="flex items-center justify-center mb-2">
                      <MapPin className="h-5 w-5 text-blue-500 mr-1" />
                    </div>
                    <div className="text-2xl font-bold text-gray-900">
                      {formatDistance(stats.distance_meters)}
                    </div>
                    <div className="text-sm text-gray-500">Distance</div>
                  </div>
                  
                  <div className="text-center">
                    <div className="flex items-center justify-center mb-2">
                      <Clock className="h-5 w-5 text-green-500 mr-1" />
                    </div>
                    <div className="text-2xl font-bold text-gray-900">
                      {formatDuration(stats.duration_seconds)}
                    </div>
                    <div className="text-sm text-gray-500">Duration</div>
                  </div>
                  
                  <div className="text-center">
                    <div className="flex items-center justify-center mb-2">
                      <Gauge className="h-5 w-5 text-purple-500 mr-1" />
                    </div>
                    <div className="text-2xl font-bold text-gray-900">
                      {formatSpeed(stats.average_speed_ms)}
                    </div>
                    <div className="text-sm text-gray-500">Avg Speed</div>
                  </div>
                  
                  <div className="text-center">
                    <div className="flex items-center justify-center mb-2">
                      <Mountain className="h-5 w-5 text-orange-500 mr-1" />
                    </div>
                    <div className="text-2xl font-bold text-gray-900">
                      {Math.round(stats.elevation_gain_meters)}m
                    </div>
                    <div className="text-sm text-gray-500">Elevation</div>
                  </div>
                </div>
                
                <div className="border-t pt-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <Footprints className="h-5 w-5 text-red-500 mr-2" />
                      <span className="text-sm text-gray-600">Calories</span>
                    </div>
                    <span className="font-semibold">{stats.calories_burned} cal</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* GPS Info */}
            {currentPosition && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center justify-between">
                    GPS Info
                    {isDemoMode && (
                      <Badge variant="outline" className="text-xs">
                        Demo Data
                      </Badge>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Accuracy:</span>
                    <span>{Math.round(currentPosition.accuracy)}m</span>
                  </div>
                  {currentPosition.altitude && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Altitude:</span>
                      <span>{Math.round(currentPosition.altitude)}m</span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Coordinates:</span>
                    <span className="text-xs">
                      {currentPosition.lat.toFixed(6)}, {currentPosition.lng.toFixed(6)}
                    </span>
                  </div>
                  {isDemoMode && (
                    <div className="bg-blue-50 p-2 rounded text-xs text-blue-800">
                      Simulated GPS coordinates from Silver Lake, LA
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Cultural POI Discoveries */}
            {discoveredPOIs.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center justify-between">
                    Cultural Discoveries
                    <Badge variant="secondary">{discoveredPOIs.length}</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="text-sm text-gray-600 mb-3">
                    POIs discovered during your walk
                  </div>
                  {discoveredPOIs.slice(0, 3).map((poi) => (
                    <div key={poi.id} className="flex items-center gap-3 p-2 bg-blue-50 rounded-lg">
                      <span className="text-lg">{
                        poi.category === 'food' ? '🍕' :
                        poi.category === 'art' ? '🎨' :
                        poi.category === 'business' ? '🛍️' : '📍'
                      }</span>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm truncate">{poi.name}</div>
                        <div className="text-xs text-gray-600 truncate">{poi.subcategory.replace('-', ' ')}</div>
                      </div>
                      {poi.user_ratings > 0 && (
                        <div className="flex items-center gap-1 text-xs">
                          <Star className="h-3 w-3 fill-yellow-400 text-yellow-400" />
                          {poi.user_ratings.toFixed(1)}
                        </div>
                      )}
                    </div>
                  ))}
                  {discoveredPOIs.length > 3 && (
                    <div className="text-center text-sm text-gray-600">
                      +{discoveredPOIs.length - 3} more discoveries
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>

      {/* POI Notifications */}
      {poiNotifications.length > 0 && (
        <div className="fixed top-20 right-4 z-50 space-y-2">
          {poiNotifications.map((notification) => (
            <div
              key={notification.id}
              className="bg-white border border-blue-200 rounded-lg shadow-lg p-4 max-w-sm transition-all duration-300 ease-in-out transform translate-x-0"
              style={{
                animation: 'slideInRight 0.3s ease-out'
              }}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3 flex-1">
                  <span className="text-2xl">{
                    notification.poi.category === 'food' ? '🍕' :
                    notification.poi.category === 'art' ? '🎨' :
                    notification.poi.category === 'business' ? '🛍️' :
                    notification.poi.category === 'architecture' ? '🏛️' : '📍'
                  }</span>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm text-blue-900">
                      Cultural spot nearby!
                    </div>
                    <div className="font-medium text-gray-900 truncate">
                      {notification.poi.name}
                    </div>
                    <div className="text-sm text-gray-600">
                      {notification.poi.subcategory.replace('-', ' ')}
                    </div>
                    {notification.poi.insider_tips && (
                      <div className="text-xs text-blue-700 mt-1 line-clamp-2">
                        💡 {notification.poi.insider_tips}
                      </div>
                    )}
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => dismissPOINotification(notification.id)}
                  className="text-gray-400 hover:text-gray-600 p-1"
                >
                  ✕
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Complete Walk Modal */}
      {showCompleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white p-6 border-b border-gray-200 rounded-t-lg">
              <h3 className="text-xl font-semibold">Save Your Walk</h3>
              <p className="text-gray-600 text-sm mt-1">
                Add details to help others discover your walk
              </p>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Basic Information */}
              <div className="space-y-4">
                <h4 className="font-medium text-lg text-gray-900">Basic Information</h4>
                
                <div>
                  <Label htmlFor="title" className="text-sm font-medium text-gray-700">
                    Walk Title *
                  </Label>
                  <Input
                    id="title"
                    value={walkDetails.title}
                    onChange={(e) => handleWalkDetailChange('title', e.target.value)}
                    className="mt-1"
                    placeholder="Give your walk a memorable name"
                    required
                  />
                </div>
                
                <div>
                  <Label htmlFor="description" className="text-sm font-medium text-gray-700">
                    Description *
                  </Label>
                  <Textarea
                    id="description"
                    value={walkDetails.description}
                    onChange={(e) => handleWalkDetailChange('description', e.target.value)}
                    className="mt-1"
                    rows="3"
                    placeholder="Tell others what makes this walk special..."
                    required
                  />
                </div>
                
                <div>
                  <Label htmlFor="practicalSummary" className="text-sm font-medium text-gray-700">
                    Practical Summary
                  </Label>
                  <Textarea
                    id="practicalSummary"
                    value={walkDetails.practicalSummary}
                    onChange={(e) => handleWalkDetailChange('practicalSummary', e.target.value)}
                    className="mt-1"
                    rows="2"
                    placeholder="A brief overview of the walk (key features, ideal for...)"
                  />
                </div>
              </div>

              {/* Route Details */}
              <div className="space-y-4">
                <h4 className="font-medium text-lg text-gray-900">Route Details</h4>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="startingPoint" className="text-sm font-medium text-gray-700">
                      Starting Point
                    </Label>
                    <Input
                      id="startingPoint"
                      value={walkDetails.startingPoint}
                      onChange={(e) => handleWalkDetailChange('startingPoint', e.target.value)}
                      className="mt-1"
                      placeholder="Where does the walk begin?"
                    />
                  </div>
                  <div>
                    <Label htmlFor="endingPoint" className="text-sm font-medium text-gray-700">
                      Ending Point
                    </Label>
                    <Input
                      id="endingPoint"
                      value={walkDetails.endingPoint}
                      onChange={(e) => handleWalkDetailChange('endingPoint', e.target.value)}
                      className="mt-1"
                      placeholder="Where does the walk end?"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="category" className="text-sm font-medium text-gray-700">
                      Category
                    </Label>
                    <Select 
                      value={walkDetails.category} 
                      onValueChange={(value) => handleWalkDetailChange('category', value)}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map((category) => (
                          <SelectItem key={category} value={category}>
                            {category}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="difficulty" className="text-sm font-medium text-gray-700">
                      Difficulty
                    </Label>
                    <Select 
                      value={walkDetails.difficulty} 
                      onValueChange={(value) => handleWalkDetailChange('difficulty', value)}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Select difficulty" />
                      </SelectTrigger>
                      <SelectContent>
                        {difficulties.map((difficulty) => (
                          <SelectItem key={difficulty} value={difficulty}>
                            {difficulty}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div>
                  <Label htmlFor="transitAccess" className="text-sm font-medium text-gray-700">
                    Public Transit Access
                  </Label>
                  <Input
                    id="transitAccess"
                    value={walkDetails.transitAccess}
                    onChange={(e) => handleWalkDetailChange('transitAccess', e.target.value)}
                    className="mt-1"
                    placeholder="e.g., Metro Bus 2, 4, 302 to Silver Lake Blvd"
                  />
                </div>
              </div>

              {/* Photos */}
              <div className="space-y-4">
                <h4 className="font-medium text-lg text-gray-900">Photos</h4>
                
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
                  <Camera className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-gray-600 text-sm mb-2">Upload photos from your walk</p>
                  <input
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={handlePhotoUpload}
                    className="hidden"
                    id="photo-upload"
                  />
                  <Label htmlFor="photo-upload">
                    <Button type="button" variant="outline" size="sm" className="cursor-pointer">
                      <Upload className="mr-2 h-4 w-4" />
                      Choose Photos
                    </Button>
                  </Label>
                </div>
                
                {photos.length > 0 && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {photos.map((photo) => (
                      <div key={photo.id} className="relative">
                        <img
                          src={photo.url}
                          alt="Walk photo"
                          className="w-full h-20 object-cover rounded-lg"
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removePhoto(photo.id)}
                          className="absolute top-1 right-1 h-6 w-6 p-0 bg-black/50 hover:bg-black/70 text-white"
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Additional Details */}
              <div className="space-y-4">
                <h4 className="font-medium text-lg text-gray-900">Additional Details</h4>
                
                <div>
                  <Label htmlFor="bestTimes" className="text-sm font-medium text-gray-700">
                    Best Times to Visit
                  </Label>
                  <Textarea
                    id="bestTimes"
                    value={walkDetails.bestTimes}
                    onChange={(e) => handleWalkDetailChange('bestTimes', e.target.value)}
                    className="mt-1"
                    rows="2"
                    placeholder="e.g., Early morning (7-9 AM) or golden hour (5-7 PM)"
                  />
                </div>
                
                {/* Accessibility Features */}
                <div>
                  <Label className="text-sm font-medium text-gray-700">Accessibility Features</Label>
                  <div className="mt-2 space-y-2">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="strollerFriendly"
                        checked={walkDetails.accessibility.strollerFriendly}
                        onCheckedChange={(checked) => handleWalkDetailChange('accessibility.strollerFriendly', checked)}
                      />
                      <Label htmlFor="strollerFriendly" className="text-sm">Stroller Friendly</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="wheelchairAccessible"
                        checked={walkDetails.accessibility.wheelchairAccessible}
                        onCheckedChange={(checked) => handleWalkDetailChange('accessibility.wheelchairAccessible', checked)}
                      />
                      <Label htmlFor="wheelchairAccessible" className="text-sm">Wheelchair Accessible</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="parkingAvailable"
                        checked={walkDetails.accessibility.parkingAvailable}
                        onCheckedChange={(checked) => handleWalkDetailChange('accessibility.parkingAvailable', checked)}
                      />
                      <Label htmlFor="parkingAvailable" className="text-sm">Parking Available</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="restrooms"
                        checked={walkDetails.accessibility.restrooms}
                        onCheckedChange={(checked) => handleWalkDetailChange('accessibility.restrooms', checked)}
                      />
                      <Label htmlFor="restrooms" className="text-sm">Restrooms Available</Label>
                    </div>
                  </div>
                  
                  <div className="mt-3">
                    <Label htmlFor="shadeCoverage" className="text-sm font-medium text-gray-700">
                      Shade Coverage
                    </Label>
                    <Select 
                      value={walkDetails.accessibility.shadeCoverage} 
                      onValueChange={(value) => handleWalkDetailChange('accessibility.shadeCoverage', value)}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Select shade coverage" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Excellent">Excellent</SelectItem>
                        <SelectItem value="Good">Good</SelectItem>
                        <SelectItem value="Moderate">Moderate</SelectItem>
                        <SelectItem value="Limited">Limited</SelectItem>
                        <SelectItem value="None">None</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Walk Highlights */}
              <div className="space-y-4">
                <HighlightsManager
                  selectedHighlights={selectedHighlights}
                  onHighlightsChange={setSelectedHighlights}
                  discoveredPOIs={discoveredPOIs}
                  walkCoordinates={coordinates}
                  title="Walk Highlights"
                />
              </div>

              {/* Privacy Settings */}
              <div className="space-y-4">
                <h4 className="font-medium text-lg text-gray-900">Privacy & Sharing</h4>
                
                <div>
                  <Label htmlFor="privacy" className="text-sm font-medium text-gray-700">
                    Privacy
                  </Label>
                  <Select 
                    value={walkDetails.privacy} 
                    onValueChange={(value) => handleWalkDetailChange('privacy', value)}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select privacy level" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="private">Private (Only you can see)</SelectItem>
                      <SelectItem value="friends">Friends (Visible to friends)</SelectItem>
                      <SelectItem value="public">Public (Visible to everyone)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="make_public"
                    checked={walkDetails.make_public}
                    onCheckedChange={(checked) => handleWalkDetailChange('make_public', checked)}
                  />
                  <Label htmlFor="make_public" className="text-sm">
                    Add to community walks for others to discover
                  </Label>
                </div>
              </div>
            </div>
            
            {/* Modal Footer */}
            <div className="sticky bottom-0 bg-gray-50 px-6 py-4 border-t border-gray-200 rounded-b-lg">
              <div className="flex space-x-3">
                <Button
                  onClick={() => setShowCompleteModal(false)}
                  variant="outline"
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={completeWalk}
                  className="flex-1 bg-blue-600 hover:bg-blue-700"
                  disabled={!walkDetails.title.trim()}
                >
                  <Save className="h-4 w-4 mr-2" />
                  Save Walk
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
      </div>
      </div>

      <Footer />
    </div>
  );
};

export default LiveTrackingPage;